<template>
  <div>
    <Pane v-model:value="computedPaneIsOpen"
          :tabs="tabs"
          temporary
          :current-tab="currentTab"
          data-locator="bibles-share-pane"
          @update:modelValue="closePane"
    >
      <template #content-share>
        <v-row>
          <v-col>
            <DocumentSharePicker v-if="paneIsOpen && !bibleRightsPending"
                                 :force-pm-selection="false"
                                 users-only
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <VisualTextEditor
              v-model:value="customMessage"
              ref="customMessageEditor"
              counter="footer"
              :error-messages="customMessageErrors"
              :max-length="customMessageMaxLength"
              data-locator="optional-message-editor"
              :placeholder="$t('common.optionalMessageWithCopy')"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <app-button fullWidth
                        type="outlined"
                        variant="neutral"
                        :disabled="loading"
                        data-locator="cancel-button"
                        @click="closePane"
            >
              {{ $t('common.cancel') }}
          </app-button>
          </v-col>
          <v-col>
            <app-button fullWidth
                       :loading="loading"
                       :disabled="shareDisabled"
                        data-locator="confirm-button"
                       @click="preparePostBibleRights"
            >
              {{ $t('common.share') }}
            </app-button>
          </v-col>
        </v-row>
      </template>
    </Pane>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import { toast } from '@/common/utils/toast'
import { surveyCanBeTriggered, SurveyId, triggerSurvey } from '@/plugins/satismeter'
import { BiblesActionTypes } from '@/store/modules/bibles/action_types'

import DocumentSharePicker from '../../common/document-share/DocumentSharePicker.vue'
import Pane from '../../common/Pane.vue'
import { escapeHtml } from '../../common/utils/strings'
import VisualTextEditor from '../../common/visual-text-editor/VisualTextEditor.vue'
import { DocumentSharePickerActionTypes } from '../../store/modules/document-share-picker/action_types'

const CUSTOM_MESSAGE_MAX_LENGTH = 400

export default {
  name: 'BiblesSharePane',
  emits: ['close-share-pane','share-success'],
  components: { VisualTextEditor, DocumentSharePicker, Pane },
  props: {
    bible: {
      type: Object,
    },
    mnemo: {
      type: String,
      required: true,
    },
    paneIsOpen: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      tabs: [
        {
          title: this.$t('common.share'),
          id: 'share',
        },
      ],
      currentTab: 'share',
      customMessage: '',
      customMessageMaxLength: CUSTOM_MESSAGE_MAX_LENGTH,
      loading: false,
    }
  },
  computed: {
    ...mapState('documentSharePicker', ['selectedMembers']),
    ...mapState('groups', ['groups']),
    ...mapState('bibles', ['biblePendingIds']),
    ...mapState('user', ['profile']),
    computedPaneIsOpen: {
      get () {
        return this.paneIsOpen
      },
      set (value) {
        value
          ? this.openPane()
          : this.closePane()
      },
    },
    customMessageLength () {
      return escapeHtml(this.customMessage).length
    },
    customMessageIsTooLong () {
      return this.customMessageLength > this.customMessageMaxLength
    },
    customMessageErrors () {
      const errors = []

      if (this.customMessageIsTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    shareDisabled () {
      return this.customMessageIsTooLong
    },
    bibleRightsPending () {
      return this.biblePendingIds.includes(this.bible.id)
    },
  },
  created () {
    this.GET_GROUPS(this.mnemo)
  },
  methods: {
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
      DocumentSharePickerActionTypes.DISABLE_MEMBERS,
    ]),
    ...mapActions('bibles', [BiblesActionTypes.POST_BIBLE_RIGHTS, BiblesActionTypes.GET_BIBLE]),
    ...mapActions('groups', ['GET_GROUPS']),
    openPane () {
      this.INIT_SELECTED_GROUPS([])
      this.INIT_SELECTED_MEMBERS([])
      this.initBibleRights()
      this.paneIsOpen = true
    },
    async initBibleRights () {
      try {
        const response = await this.GET_BIBLE({
          mnemo: this.mnemo,
          bibleId: this.bible.id,
        })

        if (response.data.rights?.read?.users.length > 0 && this.groups.length > 0) {
          const membersToDisable = this.groups.flatMap(group => group.members)
            .filter(member => response.data.rights.read.users.some(o => o === member.id))

          if (membersToDisable.length) {
            this.DISABLE_MEMBERS(membersToDisable)
          }
        }
      } catch (error) {
        console.error(error)
        toast.error(this.$t('project.bibles.BiblesSharePane.getBibleRightsError'))
      }
    },
    closePane () {
      this.$emit('close-share-pane');
      this.customMessage = ''
      this.$refs.customMessageEditor?.clear()
      this.loading = false
    },
    async preparePostBibleRights () {
      try {
        this.loading = true
        await this.POST_BIBLE_RIGHTS({
          mnemo: this.mnemo,
          bibleId: this.bible.id,
          data: {
            withUsers: this.selectedMembers.map(o => o.id),
            notifyMessage: this.customMessage,
          },
        })
        toast.success(this.$t('project.bibles.BiblesSharePane.postBibleRightsSuccess'))
        this.$emit('share-success')

        const { id: userId, fullName, email, isSubUser } = this.profile
        if (surveyCanBeTriggered(SurveyId.CSAT, userId)) {
          triggerSurvey(SurveyId.CSAT, userId, fullName, email, isSubUser)
        }
        if (surveyCanBeTriggered(SurveyId.NPS, userId)) {
          triggerSurvey(SurveyId.NPS, userId, fullName, email, isSubUser)
        }
      } catch (e) {
        toast.error(this.$t('project.bibles.BiblesSharePane.postBibleRightsError'))
      } finally {
        this.closePane()
      }
    },
  },
}
</script>
